<template>
  <v-text-field
      rounded="lg"
      :error-messages="error"
      :label="translation(translationList.house_surface, 'Suprafață')"
      v-model="surfaceString"
      name="surface"
      :rules="surfaceValidationRules"
      @input="onInput"
      variant="outlined"
      bg-color="surface"
      :tile="true"
      suffix="mp"
      type="number"
  >
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: Number as () => number | null,
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const surface = ref<number | null>(modelValue.value ?? null);
    const surfaceString = ref<string | null>(modelValue.value !== null ? String(modelValue.value) : null);
    const error = ref<string[]>([]);

    watch(surface, (newValue) => {
      const isValid = validateSurface(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', null);
      }
      emit('validation-house-status', { field: 'surface', isValid });
    });

    watch(surfaceString, (newValue) => {
      const parsedValue = parseFloat(newValue as string);
      if (!isNaN(parsedValue)) {
        surface.value = parsedValue;
      } else {
        surface.value = null;
      }
    });

    function validateSurface(value: number | null, showErrors?: boolean): boolean {
      error.value = [];
      if (value !== null) {
        if (showErrors) {
          const errors = surfaceValidationRules
              .map(rule => rule(value))
              .filter(result => result !== true) as string[];
          if (errors.length > 0) {
            error.value = errors;
            return false;
          }
        }
        return surfaceValidationRules.every(rule => rule(value) === true);
      } else if (showErrors) {
        error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        return false;
      }
      return false;
    }

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const surfaceValidationRules = [
      (v: number) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: number) => v >= 0 || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
    ];

    const onInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      surfaceString.value = input.value;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      // Listen for validate event
      window.addEventListener('validate-house', (data) => {
        if (data.detail && data.detail === 'surface') {
          const isValid = validateSurface(surface.value, true);
          if (isValid) {
            emit('validation-house-status', { field: 'surface', isValid });
          }
        }
      });
    });

    watch(modelValue, (newValue) => {
      const formattedValue = newValue !== null ? String(newValue) : null;
      if (formattedValue !== surfaceString.value) {
        surfaceString.value = formattedValue;
      }
    });

    return {
      surface,
      surfaceString,
      surfaceValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>